import { Link } from '@/components/LocalizedLink';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import DirectAnswerBlock from '@/components/knowledge/DirectAnswerBlock';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAiAnswerEntryById, getRelatedAiAnswerEntries } from '@/lib/aiAnswersCatalog';
import { FIGTURES_BRAND_NAME_DISAMBIGUATION } from '@/lib/brandCopy';
import { resolvePortfolioProjectKey } from '@/lib/portfolioProjects';
import { SITE_URL, buildAbsoluteUrl, truncateMeta } from '@/lib/seo';
import {
  buildArticleSchema,
  buildAuthorGraphNodes,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildSpeakableSchema,
} from '@/lib/schema';

const FIGTURES_BRAND_SERVICES = {
  tr: [
    { name: 'AI Geliştirme', path: '/services/ai-development' },
    { name: 'Custom Software', path: '/services/custom-software-development' },
    { name: 'SaaS / MVP Geliştirme', path: '/services/saas-mvp-development' },
    { name: 'Mobil Uygulama', path: '/services/mobile-app-development' },
    { name: 'ChatGPT Entegrasyonu', path: '/solutions/chatgpt-integration-development' },
    { name: 'Web Geliştirme', path: '/services/web-development' },
  ],
  en: [
    { name: 'AI Development', path: '/services/ai-development' },
    { name: 'Custom Software Development', path: '/services/custom-software-development' },
    { name: 'SaaS / MVP Development', path: '/services/saas-mvp-development' },
    { name: 'Mobile App Development', path: '/services/mobile-app-development' },
    { name: 'ChatGPT Integration', path: '/solutions/chatgpt-integration-development' },
    { name: 'Web Development', path: '/services/web-development' },
  ],
} as const;

const AiAnswerDetailPage = () => {
  const { lang, t } = useLanguage();
  const { slug } = useParams<{ slug: string }>();
  const entry = slug ? getAiAnswerEntryById(slug, lang) : null;

  if (!entry) {
    return (
      <main className="pt-32 pb-20">
        <SEOHead
          title={lang === 'tr' ? 'Soru bulunamadı' : 'Question not found'}
          description={lang === 'tr' ? 'Aradığınız AI cevabı bulunamadı.' : 'The AI answer you requested could not be found.'}
          canonical="/ai-answers"
          noindex
        />
        <div className="container mx-auto px-6 lg:px-12">
          <h1 className="font-display text-4xl font-bold text-foreground mb-4">
            {lang === 'tr' ? 'Soru bulunamadı' : 'Question not found'}
          </h1>
          <Link to="/ai-answers" className="inline-flex items-center gap-2 font-body text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} />
            {lang === 'tr' ? 'Tüm cevaplara dön' : 'Back to all answers'}
          </Link>
        </div>
      </main>
    );
  }

  const caseStudies = entry.caseStudySlugs.flatMap((caseSlug) => {
    const key = resolvePortfolioProjectKey(caseSlug);
    return key ? [{ slug: caseSlug, title: t(`projects.${key}.title`) }] : [];
  });

  const canonicalPath = `/ai-answers/${entry.id}`;
  const canonicalUrl = buildAbsoluteUrl(SITE_URL, canonicalPath);
  const relatedEntries = getRelatedAiAnswerEntries(entry.id, lang);
  const inLanguage = lang === 'tr' ? 'tr-TR' : 'en-US';

  const articleSchema = buildArticleSchema({
    headline: entry.questionText,
    description: entry.directAnswerText,
    path: canonicalPath,
    dateModified: entry.lastUpdated,
    inLanguage,
    authorName: null,
  });

  const faqSchema = buildFaqSchema([{ question: entry.questionText, answer: entry.directAnswerText }], {
    canonicalPath,
    inLanguage,
    locale: lang,
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: SITE_URL },
    { name: lang === 'tr' ? 'AI Cevapları' : 'AI Answers', item: `${SITE_URL}/ai-answers` },
    { name: entry.questionText, item: canonicalUrl },
  ]);

  const speakableSchema = buildSpeakableSchema({ canonicalPath, locale: lang });

  const lastUpdatedLabel = new Date(entry.lastUpdated).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <main className="pt-32 pb-20">
      <SEOHead
        title={entry.questionText}
        description={truncateMeta(entry.directAnswerText)}
        canonical={canonicalPath}
        type="article"
        jsonLd={[...buildAuthorGraphNodes(undefined, lang), articleSchema, breadcrumbSchema, faqSchema, speakableSchema]}
      />

      <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
        <Link to="/ai-answers" className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-10 group">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {lang === 'tr' ? 'Tüm AI cevapları' : 'All AI answers'}
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className="font-body text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">
            {entry.categoryText}
          </p>
          <h1 className="font-display text-3xl lg:text-5xl font-bold text-foreground leading-tight mb-4">
            {entry.questionText}
          </h1>
          <p className="font-body text-xs text-muted-foreground mb-8">
            {lang === 'tr' ? 'Son güncelleme:' : 'Last updated:'} {lastUpdatedLabel}
          </p>
          <DirectAnswerBlock answer={entry.directAnswerText} />
        </motion.header>

        {entry.id === 'what-is-figtures' && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mb-10"
          >
            <h2 className="font-display text-xl text-foreground mb-4">
              {lang === 'tr' ? 'Sunulan Hizmetler' : 'Services Offered'}
            </h2>
            <ul className="space-y-2 mb-4">
              {FIGTURES_BRAND_SERVICES[lang].map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="font-body text-sm text-foreground/90 hover:text-foreground underline underline-offset-4"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">
              {FIGTURES_BRAND_NAME_DISAMBIGUATION[lang]}
            </p>
          </motion.section>
        )}

        {entry.evidenceCitations.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mb-10"
          >
            <h2 className="font-display text-xl text-foreground mb-3">
              {lang === 'tr' ? 'Doğrulanabilir kaynaklar' : 'Verifiable sources'}
            </h2>
            <ul className="space-y-2">
              {entry.evidenceCitations.map((citation) => (
                <li key={citation.path}>
                  <Link
                    to={citation.path}
                    className="font-body text-sm text-foreground/90 hover:text-foreground underline underline-offset-4"
                  >
                    {citation.labelText}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.section>
        )}

        {entry.decisionGuideText && (
          <section className="mb-10 space-y-8" data-testid="ai-answer-decision-guide">
            <div>
              <h2 className="font-display text-xl text-foreground mb-3">
                {lang === 'tr' ? 'Karar kriterleri' : 'Decision criteria'}
              </h2>
              <ul className="list-disc pl-5 space-y-2 font-body text-sm text-foreground/85 leading-relaxed">
                {entry.decisionGuideText.criteria.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h2 className="font-display text-xl text-foreground mb-3">
                  {lang === 'tr' ? 'Kimler için uygun' : 'Good fit for'}
                </h2>
                <ul className="list-disc pl-5 space-y-2 font-body text-sm text-foreground/85 leading-relaxed">
                  {entry.decisionGuideText.fit.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="font-display text-xl text-foreground mb-3">
                  {lang === 'tr' ? 'Kimler için uygun değil' : 'Not a fit for'}
                </h2>
                <ul className="list-disc pl-5 space-y-2 font-body text-sm text-foreground/85 leading-relaxed">
                  {entry.decisionGuideText.notFit.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <h2 className="font-display text-xl text-foreground mb-3">
                {lang === 'tr' ? 'Teknik sınırlar' : 'Technical limits'}
              </h2>
              <ul className="list-disc pl-5 space-y-2 font-body text-sm text-foreground/85 leading-relaxed">
                {entry.decisionGuideText.limits.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {caseStudies.length > 0 && (
          <section className="mb-10" data-testid="ai-answer-case-studies">
            <h2 className="font-display text-xl text-foreground mb-3">
              {lang === 'tr' ? 'Yayınlanmış vakalar' : 'Published case studies'}
            </h2>
            <ul className="space-y-2">
              {caseStudies.map((item) => (
                <li key={item.slug}>
                  <Link
                    to={`/projects/${item.slug}`}
                    className="font-body text-sm text-foreground/90 hover:text-foreground underline underline-offset-4"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-10"
        >
          <h2 className="font-display text-xl text-foreground mb-3">
            {lang === 'tr' ? 'Genişletilmiş bağlam' : 'Extended context'}
          </h2>
          <p className="font-body text-base leading-relaxed text-foreground/85 mb-6">
            {entry.relatedSummaryText}
          </p>
          <Link
            to={entry.relatedPath}
            className="inline-flex items-center gap-2 rounded-xl border border-border/70 bg-card/30 px-5 py-3 font-body text-sm text-foreground hover:border-foreground/40 transition-colors"
          >
            {lang === 'tr' ? 'İlgili kaynak:' : 'Related resource:'} {entry.relatedPath}
          </Link>
        </motion.section>

        {relatedEntries.length > 0 && (
          <section className="border-t border-border/70 pt-10">
            <h2 className="font-display text-xl text-foreground mb-6">
              {lang === 'tr' ? 'Diğer sorular' : 'Related questions'}
            </h2>
            <ul className="space-y-3">
              {relatedEntries.map((related) => (
                <li key={related.id}>
                  <Link
                    to={`/ai-answers/${related.id}`}
                    className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                  >
                    {related.questionText}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
};

export default AiAnswerDetailPage;
